import { useState } from 'react';

import { useAPI, useAsyncHandler } from '~/hooks/common';
import { transformToSelectOptions } from '~/utils/common';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeDeliveryAgencyTrackingFilterOptions,
  TypeGetShippingLabelsSearchConditionsResponse,
} from '~/types/delivery-agency.types';

const useGetShippingLabelsSearchConditions = () => {
  const { runAsync } = useAsyncHandler();

  const [options, setOptions] = useState<TypeDeliveryAgencyTrackingFilterOptions>({
    searchKeywordTypes: [],
    trackingStatuses: [],
    deliveryTypes: [],
    courierCodes: [],
  });

  const getShippingLabelsSearchConditions = useAPI<unknown, TypeGetShippingLabelsSearchConditionsResponse>({
    endpoint: API_ENDPOINT.DELIVERY_AGENCY.GET_SHIPPING_LABELS_CODES,
    method: 'get',
  });

  const formatCodes = (
    data: TypeGetShippingLabelsSearchConditionsResponse['data']
  ): TypeDeliveryAgencyTrackingFilterOptions => {
    return {
      searchKeywordTypes: transformToSelectOptions(data.searchKeywordTypes, 'code', 'description'),
      trackingStatuses: transformToSelectOptions(data.shippingLabelStatuses, 'code', 'description'),
      deliveryTypes: transformToSelectOptions(data.deliveryTypes, 'code', 'description'),
      courierCodes: transformToSelectOptions(data.courierCodes, 'code', 'description'),
    };
  };

  const requestCodes = async ({
    onSuccess,
    onError,
  }: {
    onSuccess?: (data: TypeDeliveryAgencyTrackingFilterOptions) => void;
    onError?: (error: TypeErrorResponseData['error']) => void;
  }) => {
    runAsync(() => getShippingLabelsSearchConditions({}), {
      onSuccess: ({ data }) => {
        const options = formatCodes(data);
        setOptions(options);
        onSuccess?.(options);
      },
      onError: (error: TypeErrorResponseData['error']) => onError?.(error),
    });
  };

  return { requestCodes, options };
};

export default useGetShippingLabelsSearchConditions;
